import { AlertCircle, ClipboardList, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { type LotoDetail, useCambiarProcedimiento } from "@/lib/lotos";
import {
  etiquetaProcedimiento,
  etiquetaProcedimientoNoAsignable,
  type Procedimiento,
  useProcedimientos,
} from "@/lib/procedimientos";

// mensajeError traduce el fallo del cambio de procedimiento a un texto en español. El 403
// (no eres el creador ni un supervisor) y el 409 (LOTO cerrado) son los casos de negocio
// esperados; el 422 señala un procedimiento que el servidor no acepta para este LOTO: desde
// #302 eso incluye el RETIRADO, y el mensaje lo nombra con la palabra común (#303 D6).
function mensajeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "CAMBIO_PROC_NO_PERMITIDO") {
      return "No puedes cambiar el procedimiento de este LOTO: debes ser quien lo abrió o un supervisor autorizado.";
    }
    if (err.code === "LOTO_CERRADO") {
      return "El LOTO está cerrado: no admite cambiar el procedimiento.";
    }
    if (err.code === "VALIDACION") {
      return "El procedimiento seleccionado no es válido para este equipo o ha sido retirado.";
    }
    if (err.code === "NO_ENCONTRADO") {
      return "El LOTO ya no existe.";
    }
    return err.message || "No se pudo cambiar el procedimiento.";
  }
  return "No se pudo cambiar el procedimiento. Comprueba tu conexión.";
}

/**
 * CambiarProcedimientoDialog asigna a un LOTO ya creado un procedimiento LOTO de su equipo
 * (SPEC-LOTO-004, #129). Ofrece un desplegable limitado a los procedimientos VIVOS del equipo
 * del LOTO (los únicos que el servidor aceptará), preselecciona el actual y traduce los errores
 * de negocio (permiso, LOTO cerrado, procedimiento inválido). Al guardar con éxito cierra y la
 * ficha se refresca por invalidación.
 */
export function CambiarProcedimientoDialog({
  loto,
  open,
  onClose,
}: {
  loto: LotoDetail;
  open: boolean;
  onClose: () => void;
}) {
  const obraId = useObraId();
  const procedimientos = useProcedimientos(obraId);
  const cambiar = useCambiarProcedimiento(obraId);
  const [seleccion, setSeleccion] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Procedimientos del equipo del LOTO, activos: los únicos que se pueden ASIGNAR.
  const procedimientosEquipo = useMemo(
    () =>
      (procedimientos.data ?? []).filter(
        (p: Procedimiento) => p.id_equipo === loto.id_equipo && p.activo,
      ),
    [procedimientos.data, loto.id_equipo],
  );

  // Procedimiento actualmente asignado al LOTO que YA NO es re-asignable (#135): se desactivó, lo
  // movieron a otro equipo o se soft-deleteó. Se muestra como opción de solo-contexto —marcada,
  // para que el técnico vea qué hay puesto— y, sobre todo, para que la preselección tenga SIEMPRE
  // un <option> con su value y el <Select> no quede «huérfano» (warning de React). No se puede
  // volver a guardar: `confirmar` lo rechaza con un mensaje claro en vez del 422 opaco del
  // backend. Es `null` cuando no hay id asignado o cuando el asignado SÍ es asignable (en cuyo
  // caso ya tiene su <option> normal, y sin marca).
  //
  // La marca sale de la ASIGNABILIDAD, no de `activo` (#303 E3/D10): llegar aquí ya significa que
  // no se puede elegir, y solo queda decir por qué —«retirado» si el catálogo lo da de baja,
  // «no disponible» por cualquier otro motivo—. Derivarla de `activo` dejaba SIN MARCA al que
  // sigue activo pero es de otro equipo: una opción limpia que el diálogo rechaza. La precedencia
  // completa vive UNA sola vez, en `lib/procedimientos.ts` (#309 D3), compartida con el alta.
  const asignadoNoAsignable = useMemo(() => {
    if (loto.id_procedimiento == null) {
      return null;
    }
    if (procedimientosEquipo.some((p) => p.id === loto.id_procedimiento)) {
      return null;
    }
    const enLista = (procedimientos.data ?? []).find(
      (p: Procedimiento) => p.id === loto.id_procedimiento,
    );
    // Cuando el catálogo ya no lo trae, el código NO se pierde: lo trae el propio LOTO. El
    // servidor proyecta su procedimiento aunque esté dado de baja, porque es el que rigió la
    // intervención (#236), así que el diálogo nombra lo mismo que la cabecera de la ficha, unos
    // centímetros más arriba (#309 R1/R2). El «#id» queda para el LOTO que no trae bloque alguno.
    const etiqueta = etiquetaProcedimientoNoAsignable(
      loto.id_procedimiento,
      enLista,
      loto.procedimiento,
    );
    return { id: loto.id_procedimiento, etiqueta };
  }, [procedimientos.data, procedimientosEquipo, loto.id_procedimiento, loto.procedimiento]);

  // Al abrir, preselecciona el procedimiento actual del LOTO (si lo tiene) y limpia el error. El
  // value preseleccionado tiene garantizado su <option>: o es uno de los activos del equipo, o es
  // la opción de contexto `asignadoNoAsignable`. Así nunca se preselecciona un id ausente de las
  // opciones (evita el warning de React del <Select> con value sin <option>).
  useEffect(() => {
    if (open) {
      setSeleccion(loto.id_procedimiento != null ? String(loto.id_procedimiento) : "");
      setError(null);
    }
  }, [open, loto.id_procedimiento]);

  function cerrar() {
    setError(null);
    onClose();
  }

  async function confirmar() {
    const idProcedimiento = Number(seleccion);
    if (!Number.isInteger(idProcedimiento) || idProcedimiento <= 0) {
      setError("Selecciona un procedimiento.");
      return;
    }
    // El procedimiento asignado que ya no es re-asignable (retirado/no disponible) se muestra como
    // contexto pero NO puede volver a guardarse: el backend lo rechazaría con un 422 confuso. Se
    // avisa aquí, sin llegar a la red, para que el técnico elija uno vivo de la lista (#135).
    if (asignadoNoAsignable && asignadoNoAsignable.id === idProcedimiento) {
      setError("Este procedimiento ya no está disponible. Selecciona uno de la lista.");
      return;
    }
    setError(null);
    try {
      await cambiar.mutateAsync({ idLoto: loto.id, idProcedimiento });
      cerrar();
    } catch (err) {
      setError(mensajeError(err));
    }
  }

  const sinProcedimientos = procedimientosEquipo.length === 0;

  return (
    <Modal open={open} onClose={cerrar} title="Cambiar procedimiento">
      <p className="text-base text-slate-600">
        Asigna el procedimiento LOTO aplicable a este equipo. El cambio queda registrado en la
        auditoría.
      </p>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_procedimiento">Procedimiento</Label>
        <Select
          id="id_procedimiento"
          value={seleccion}
          onChange={(e) => setSeleccion(e.target.value)}
          disabled={sinProcedimientos}
        >
          <option value="" disabled>
            Selecciona un procedimiento
          </option>
          {asignadoNoAsignable ? (
            <option value={asignadoNoAsignable.id}>{asignadoNoAsignable.etiqueta}</option>
          ) : null}
          {procedimientosEquipo.map((p) => (
            <option key={p.id} value={p.id}>
              {etiquetaProcedimiento(p)}
            </option>
          ))}
        </Select>
        {sinProcedimientos ? (
          <p className="text-slate-500 text-sm">Este equipo no tiene procedimientos activos.</p>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </Alert>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={cerrar} disabled={cambiar.isPending}>
          Cancelar
        </Button>
        <Button onClick={() => void confirmar()} disabled={cambiar.isPending || sinProcedimientos}>
          {cambiar.isPending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <ClipboardList className="h-5 w-5" aria-hidden="true" />
          )}
          Guardar
        </Button>
      </div>
    </Modal>
  );
}
